/**
 * EmailRecipientsField — tag-style input for To/Cc/Bcc on the compose modal
 * (M1.14 wave 3).
 *
 * Enter and comma commit the typed value as a tag; Backspace on an empty
 * input pops the last tag. The "From list" picker is only rendered when the
 * caller passes recipient lists — admins can configure these under Settings
 * → Communication.
 *
 * Pre-commit validation runs a simple email regex; the server re-validates
 * on submission so even a paste of bad data lands gracefully.
 */
import type { CommunicationRecipientListView } from '@peerless/shared';
import { X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';


export function EmailRecipientsField({
  label,
  values,
  setValues,
  lists,
}: {
  label: string;
  values: string[];
  setValues: (v: string[]) => void;
  lists: CommunicationRecipientListView[];
}): JSX.Element {
  const [input, setInput] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const add = (raw: string): void => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(`Invalid email: ${trimmed}`);
      return;
    }
    if (values.includes(trimmed)) return;
    setValues([...values, trimmed]);
  };
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-tight text-muted">
        {label}
      </p>
      <div className="flex flex-wrap items-center gap-1.5 rounded-sm border border-border bg-bg px-2 py-1.5">
        {values.map((e) => (
          <span
            key={e}
            className="inline-flex items-center gap-1 rounded-sm border border-border bg-surface px-1.5 py-0.5 text-[11px] text-text"
          >
            {e}
            <button
              type="button"
              onClick={() => setValues(values.filter((x) => x !== e))}
              aria-label={`Remove ${e}`}
              className="text-muted hover:text-danger"
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
        <input
          type="email"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add(input);
              setInput('');
            } else if (e.key === 'Backspace' && input.length === 0 && values.length > 0) {
              setValues(values.slice(0, -1));
            }
          }}
          placeholder="email@example.com"
          className="min-w-[120px] flex-1 bg-transparent text-xs text-text placeholder:text-muted focus:outline-none"
        />
        {lists.length > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className="inline-flex items-center gap-1 rounded-sm border border-border bg-surface px-1.5 py-0.5 text-[11px] text-muted hover:bg-bg hover:text-text"
            >
              From list
            </button>
            {pickerOpen && (
              <ul className="absolute right-0 z-10 mt-1 max-h-64 w-64 overflow-y-auto rounded-sm border border-border bg-surface p-1 shadow-lg">
                {lists.map((l) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      onClick={() => {
                        const merged = Array.from(new Set([...values, ...l.emails]));
                        setValues(merged);
                        setPickerOpen(false);
                        toast.success(`Added ${l.emails.length} from "${l.name}"`);
                      }}
                      className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1 text-left text-xs text-text hover:bg-bg"
                    >
                      <span className="truncate">{l.name}</span>
                      <span className="text-[10px] text-muted">{l.emails.length}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
